import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useState } from "react";
import { ChevronDown, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { TechAnimation, TeamAnimation } from "@/components/TechAnimations";
import OptimizedImage from "@/components/ImageOptimization";
import { Badge } from "@/components/ui/badge";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";

// Import professional images
import teamworkImage from "@/assets/teamwork-collaboration.jpg";
import itServicesImage from "@/assets/it-services-modern.jpg";
import technologyImage from "@/assets/advanced-technology.jpg";
import remoteWorkImage from "@/assets/remote-work-setup.jpg";
import clientSupportImage from "@/assets/client-support-communication.jpg";
import businessConsultingImage from "@/assets/business-consulting-strategy.jpg";
import teamMeetingImage from "@/assets/teamwork-meeting.jpg";
import teamBrainstormingImage from "@/assets/teamwork-brainstorming.jpg";
import itServerRoomImage from "@/assets/it-server-room.jpg";
import innovationLabImage from "@/assets/innovation-lab.jpg";
import technologyWorkspaceImage from "@/assets/technology-workspace.jpg";
import remoteWorkOfficeImage from "@/assets/remote-work-office.jpg";

import { 
  Headphones,
  Users,
  Briefcase,
  Workflow,
  TestTube,
  FileText,
  Monitor,
  Layers,
  GitBranch,
  Rocket,
  Wrench,
  Eye,
  Lock,
  Server,
  Bot,
  Brain,
  Network,
  HardDrive,
  Wifi,
  Smartphone as Mobile,
  Tablet,
  Watch,
  Settings
} from "lucide-react";

const AllServices = () => {
  const [expandedServices, setExpandedServices] = useState<number[]>([]);

  const toggleService = (index: number) => {
    setExpandedServices(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  // Services 11-35
  const additionalServices = [
    {
      icon: Headphones,
      title: "24/7 Support & Maintenance",
      description: "Round-the-clock technical support and proactive system maintenance to ensure optimal performance.",
      features: ["24/7 monitoring", "Incident response", "Preventive maintenance"],
      category: "Support",
      image: clientSupportImage,
      details: {
        overview: "Our comprehensive support services ensure your systems run smoothly with minimal downtime and rapid issue resolution.",
        benefits: ["24/7 availability", "Fast response times", "Proactive monitoring", "Regular updates"],
        technologies: ["Monitoring tools", "Ticketing systems", "Remote support", "Automation"],
        process: ["Issue detection", "Triage & prioritization", "Resolution", "Documentation"]
      }
    },
    {
      icon: Users,
      title: "Team Augmentation",
      description: "Scale your development team quickly with skilled professionals who integrate seamlessly with your existing teams.",
      features: ["Vetted professionals", "Flexible engagement", "Quick onboarding"],
      category: "Resources",
      image: teamworkImage,
      details: {
        overview: "Access top-tier talent on-demand to scale your development capacity and fill skill gaps efficiently.",
        benefits: ["Cost-effective scaling", "Flexible contracts", "Pre-vetted talent", "Seamless integration"],
        technologies: ["Full-stack developers", "DevOps engineers", "QA specialists", "Project managers"],
        process: ["Requirements analysis", "Talent matching", "Onboarding", "Integration"]
      }
    },
    {
      icon: Briefcase,
      title: "Consulting Services",
      description: "Strategic technology consulting to help you make informed decisions and optimize your IT investments.",
      features: ["Technology roadmaps", "Architecture design", "Best practices"],
      category: "Strategy",
      image: businessConsultingImage,
      details: {
        overview: "Our expert consultants provide strategic guidance to align your technology initiatives with business objectives.",
        benefits: ["Expert guidance", "Risk mitigation", "Cost optimization", "Innovation roadmap"],
        technologies: ["Enterprise architecture", "Cloud strategy", "Digital transformation", "Technology assessment"],
        process: ["Discovery", "Analysis", "Strategy development", "Implementation planning"]
      }
    },
    {
      icon: Workflow,
      title: "Business Process Automation",
      description: "Streamline operations and reduce manual work with intelligent automation solutions.",
      features: ["Workflow automation", "RPA", "Process optimization"],
      category: "Automation",
      image: technologyImage,
      details: {
        overview: "Transform your business operations with automation that increases efficiency and reduces human error.",
        benefits: ["Increased efficiency", "Cost reduction", "Error elimination", "Scalability"],
        technologies: ["UiPath", "Blue Prism", "Automation Anywhere", "Power Automate"],
        process: ["Process mapping", "Automation design", "Implementation", "Optimization"]
      }
    },
    {
      icon: TestTube,
      title: "Quality Assurance",
      description: "Comprehensive testing services to ensure your software meets the highest quality standards.",
      features: ["Automated testing", "Manual testing", "Performance testing"],
      category: "Quality",
      image: itServicesImage,
      details: {
        overview: "Rigorous testing methodologies ensure your applications are reliable, secure, and perform optimally.",
        benefits: ["Bug-free releases", "Performance validation", "Security assurance", "User satisfaction"],
        technologies: ["Selenium", "JUnit", "TestNG", "JMeter"],
        process: ["Test planning", "Test execution", "Defect tracking", "Reporting"]
      }
    },
    {
      icon: FileText,
      title: "Technical Documentation",
      description: "Clear, comprehensive documentation that makes your systems easier to understand and maintain.",
      features: ["API documentation", "User guides", "System architecture"],
      category: "Documentation",
      image: teamMeetingImage,
      details: {
        overview: "Professional documentation services that ensure knowledge transfer and system maintainability.",
        benefits: ["Knowledge retention", "Easier onboarding", "Reduced support burden", "Compliance support"],
        technologies: ["Confluence", "Swagger", "GitBook", "ReadTheDocs"],
        process: ["Information gathering", "Documentation creation", "Review", "Maintenance"]
      }
    },
    {
      icon: Monitor,
      title: "Enterprise Solutions",
      description: "Scalable enterprise-grade solutions designed to support large organizations and complex operations.",
      features: ["Enterprise architecture", "Scalability", "Integration"],
      category: "Enterprise",
      image: itServerRoomImage,
      details: {
        overview: "Build robust enterprise systems that scale with your business and integrate seamlessly with existing infrastructure.",
        benefits: ["High scalability", "Robust security", "Integration capabilities", "Performance"],
        technologies: ["Microservices", "Enterprise service bus", "API gateways", "Load balancers"],
        process: ["Requirements gathering", "Architecture design", "Development", "Deployment"]
      }
    },
    {
      icon: Layers,
      title: "System Integration",
      description: "Connect disparate systems and applications to create a unified, efficient technology ecosystem.",
      features: ["API integration", "Data synchronization", "Legacy system connectivity"],
      category: "Integration",
      image: technologyWorkspaceImage,
      details: {
        overview: "Seamlessly integrate your various systems and applications to improve data flow and operational efficiency.",
        benefits: ["Unified systems", "Data consistency", "Improved workflows", "Reduced silos"],
        technologies: ["REST APIs", "GraphQL", "Message queues", "ESB"],
        process: ["System analysis", "Integration design", "Implementation", "Testing"]
      }
    },
    {
      icon: GitBranch,
      title: "Microservices Architecture",
      description: "Design and implement scalable microservices architectures for flexible, maintainable applications.",
      features: ["Service decomposition", "Container orchestration", "Service mesh"],
      category: "Architecture",
      image: innovationLabImage,
      details: {
        overview: "Transform monolithic applications into flexible, scalable microservices architectures.",
        benefits: ["Independent deployment", "Technology flexibility", "Fault isolation", "Scalability"],
        technologies: ["Docker", "Kubernetes", "Istio", "Consul"],
        process: ["Domain analysis", "Service design", "Implementation", "Orchestration"]
      }
    },
    {
      icon: GitBranch,
      title: "Version Control & Collaboration",
      description: "Implement robust version control systems and collaboration workflows for efficient team development.",
      features: ["Git workflows", "Code reviews", "Branch strategies"],
      category: "Development",
      image: teamBrainstormingImage,
      details: {
        overview: "Establish best practices for code management and team collaboration using modern version control systems.",
        benefits: ["Code quality", "Team collaboration", "Change tracking", "Rollback capability"],
        technologies: ["Git", "GitHub", "GitLab", "Bitbucket"],
        process: ["Repository setup", "Workflow design", "Training", "Monitoring"]
      }
    },
    {
      icon: Eye,
      title: "Performance Optimization",
      description: "Identify and eliminate performance bottlenecks to ensure your applications run at peak efficiency.",
      features: ["Code profiling", "Database tuning", "Caching strategies"],
      category: "Optimization",
      image: remoteWorkImage,
      details: {
        overview: "Comprehensive performance analysis and optimization to deliver fast, responsive applications.",
        benefits: ["Faster load times", "Better user experience", "Cost reduction", "Scalability"],
        technologies: ["Profiling tools", "CDN", "Caching layers", "Load balancers"],
        process: ["Performance audit", "Bottleneck identification", "Optimization", "Validation"]
      }
    },
    {
      icon: Rocket,
      title: "Deployment & Delivery",
      description: "Streamlined deployment processes and continuous delivery pipelines for rapid, reliable releases.",
      features: ["CI/CD pipelines", "Automated deployment", "Blue-green deployment"],
      category: "DevOps",
      image: remoteWorkOfficeImage,
      details: {
        overview: "Implement modern deployment strategies that enable frequent, reliable software releases.",
        benefits: ["Faster releases", "Reduced downtime", "Rollback capability", "Automated testing"],
        technologies: ["Jenkins", "GitLab CI", "CircleCI", "Spinnaker"],
        process: ["Pipeline design", "Automation setup", "Testing", "Monitoring"]
      }
    },
    {
      icon: Wrench,
      title: "System Maintenance",
      description: "Proactive maintenance services to keep your systems running smoothly and prevent issues.",
      features: ["Regular updates", "System health checks", "Performance monitoring"],
      category: "Maintenance",
      image: itServerRoomImage,
      details: {
        overview: "Comprehensive maintenance services ensure system reliability and longevity.",
        benefits: ["Reduced downtime", "Extended system life", "Preventive care", "Cost savings"],
        technologies: ["Monitoring tools", "Automation scripts", "Update management", "Backup systems"],
        process: ["Health assessment", "Maintenance planning", "Execution", "Reporting"]
      }
    },
    {
      icon: Eye,
      title: "Monitoring & Analytics",
      description: "Real-time monitoring and analytics to provide insights into system performance and user behavior.",
      features: ["Real-time monitoring", "Log aggregation", "Alerting"],
      category: "Monitoring",
      image: technologyImage,
      details: {
        overview: "Comprehensive monitoring solutions that provide visibility into your entire technology stack.",
        benefits: ["Early issue detection", "Performance insights", "Data-driven decisions", "SLA compliance"],
        technologies: ["Prometheus", "Grafana", "ELK Stack", "New Relic"],
        process: ["Monitoring setup", "Alert configuration", "Dashboard creation", "Analysis"]
      }
    },
    {
      icon: Lock,
      title: "Data Security & Privacy",
      description: "Protect sensitive data with comprehensive security measures and privacy compliance solutions.",
      features: ["Data encryption", "Privacy compliance", "Access control"],
      category: "Security",
      image: clientSupportImage,
      details: {
        overview: "Ensure your data is protected and your organization complies with privacy regulations.",
        benefits: ["Data protection", "Regulatory compliance", "Risk mitigation", "Customer trust"],
        technologies: ["Encryption tools", "Key management", "DLP solutions", "GDPR tools"],
        process: ["Risk assessment", "Security implementation", "Compliance audit", "Monitoring"]
      }
    },
    {
      icon: Server,
      title: "Infrastructure Management",
      description: "Manage and optimize your IT infrastructure for reliability, performance, and cost-efficiency.",
      features: ["Infrastructure automation", "Capacity planning", "Cost optimization"],
      category: "Infrastructure",
      image: itServerRoomImage,
      details: {
        overview: "Professional infrastructure management ensures your IT resources are optimized and reliable.",
        benefits: ["High availability", "Cost optimization", "Scalability", "Performance"],
        technologies: ["Terraform", "Ansible", "CloudFormation", "Infrastructure monitoring"],
        process: ["Infrastructure assessment", "Optimization planning", "Implementation", "Ongoing management"]
      }
    },
    {
      icon: Bot,
      title: "Chatbot Development",
      description: "Intelligent chatbots that enhance customer engagement and automate support interactions.",
      features: ["Natural language processing", "Multi-channel support", "AI-powered responses"],
      category: "AI",
      image: innovationLabImage,
      details: {
        overview: "Build conversational AI solutions that improve customer service and reduce support costs.",
        benefits: ["24/7 availability", "Instant responses", "Cost reduction", "Scalability"],
        technologies: ["Dialogflow", "Rasa", "Microsoft Bot Framework", "Watson Assistant"],
        process: ["Intent design", "Bot development", "Training", "Deployment"]
      }
    },
    {
      icon: Brain,
      title: "Cognitive Services",
      description: "Leverage advanced AI capabilities for vision, speech, language, and decision-making.",
      features: ["Computer vision", "Speech recognition", "Language understanding"],
      category: "AI",
      image: technologyWorkspaceImage,
      details: {
        overview: "Integrate powerful AI services to add intelligent capabilities to your applications.",
        benefits: ["Enhanced capabilities", "Improved accuracy", "Automation", "Innovation"],
        technologies: ["Azure Cognitive Services", "Google Cloud AI", "AWS AI Services", "Custom models"],
        process: ["Use case identification", "Service integration", "Training", "Optimization"]
      }
    },
    {
      icon: Network,
      title: "Network Solutions",
      description: "Design and implement secure, high-performance network infrastructure.",
      features: ["Network design", "SD-WAN", "Network security"],
      category: "Infrastructure",
      image: remoteWorkImage,
      details: {
        overview: "Build robust network infrastructure that supports your business operations securely and efficiently.",
        benefits: ["High performance", "Secure connectivity", "Reliability", "Scalability"],
        technologies: ["Cisco", "Palo Alto", "SD-WAN", "Network monitoring"],
        process: ["Network assessment", "Design", "Implementation", "Monitoring"]
      }
    },
    {
      icon: HardDrive,
      title: "Data Migration",
      description: "Seamlessly migrate your data to new systems or cloud platforms with zero data loss.",
      features: ["Zero downtime migration", "Data validation", "Legacy system migration"],
      category: "Data",
      image: teamMeetingImage,
      details: {
        overview: "Expert data migration services ensure safe, accurate transfer of your critical business data.",
        benefits: ["Data integrity", "Minimal downtime", "Risk mitigation", "Validation"],
        technologies: ["ETL tools", "Data validation", "Migration scripts", "Backup systems"],
        process: ["Migration planning", "Data mapping", "Testing", "Execution"]
      }
    },
    {
      icon: Wifi,
      title: "IoT Solutions",
      description: "Connect and manage IoT devices with secure, scalable IoT platforms and applications.",
      features: ["Device management", "Data analytics", "Edge computing"],
      category: "IoT",
      image: technologyImage,
      details: {
        overview: "Build comprehensive IoT solutions that connect devices, collect data, and drive insights.",
        benefits: ["Real-time monitoring", "Predictive maintenance", "Operational efficiency", "Data insights"],
        technologies: ["Azure IoT", "AWS IoT", "MQTT", "Edge computing"],
        process: ["IoT strategy", "Platform setup", "Device integration", "Analytics"]
      }
    },
    {
      icon: Mobile,
      title: "Progressive Web Apps",
      description: "Build fast, reliable web applications that work offline and provide app-like experiences.",
      features: ["Offline functionality", "Push notifications", "App-like experience"],
      category: "Mobile",
      image: innovationLabImage,
      details: {
        overview: "Create web applications that combine the best of web and mobile apps.",
        benefits: ["Cross-platform", "Offline capability", "Fast loading", "Installable"],
        technologies: ["Service Workers", "Web App Manifest", "IndexedDB", "Push API"],
        process: ["Requirements analysis", "PWA development", "Testing", "Deployment"]
      }
    },
    {
      icon: Tablet,
      title: "Cross-Platform Development",
      description: "Build applications that run seamlessly across multiple platforms from a single codebase.",
      features: ["Single codebase", "Native performance", "Platform-specific features"],
      category: "Mobile",
      image: remoteWorkOfficeImage,
      details: {
        overview: "Develop once and deploy everywhere with modern cross-platform development frameworks.",
        benefits: ["Cost efficiency", "Faster development", "Consistent UX", "Easy maintenance"],
        technologies: ["React Native", "Flutter", "Xamarin", "Ionic"],
        process: ["Platform analysis", "Development", "Testing", "Deployment"]
      }
    },
    {
      icon: Watch,
      title: "Wearable App Development",
      description: "Create innovative applications for smartwatches and wearable devices.",
      features: ["Health tracking", "Notifications", "Voice control"],
      category: "Mobile",
      image: teamBrainstormingImage,
      details: {
        overview: "Develop engaging wearable applications that leverage device capabilities and sensors.",
        benefits: ["Enhanced user experience", "Real-time data", "Health integration", "Convenience"],
        technologies: ["WatchOS", "Wear OS", "Fitbit SDK", "Samsung Tizen"],
        process: ["Concept design", "Development", "Testing", "App store submission"]
      }
    },
    {
      icon: Settings,
      title: "API Development & Management",
      description: "Design, build, and manage robust APIs that power your applications and integrations.",
      features: ["RESTful APIs", "GraphQL", "API gateway", "Documentation"],
      category: "Development",
      image: technologyWorkspaceImage,
      details: {
        overview: "Create scalable, secure APIs that enable seamless integration and data exchange.",
        benefits: ["System integration", "Scalability", "Security", "Developer experience"],
        technologies: ["Node.js", "API Gateway", "Swagger", "Postman"],
        process: ["API design", "Development", "Testing", "Documentation"]
      }
    }
  ];

  const categories = [...new Set(additionalServices.map(service => service.category))];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Header */}
      <section className="py-12 md:py-16 bg-gradient-to-b from-primary/5 to-background border-b border-border">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/#services" className="inline-flex items-center gap-2 text-primary hover:underline mb-6">
            <ArrowLeft className="h-4 w-4" />
            Back to Main Services
          </Link>
          <TeamAnimation type="team-formation">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-primary via-primary/80 to-primary bg-clip-text text-transparent">
              Complete Services Portfolio
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl">
              Explore our full range of specialized services designed to drive your business forward
            </p>
          </TeamAnimation>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-12 md:py-16 lg:py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Category Filter */}
          <div className="flex flex-wrap gap-3 justify-center mb-12">
            <Badge variant="outline" className="px-4 py-2 text-sm">
              All Services
            </Badge>
            {categories.map((category, index) => (
              <Badge key={index} variant="outline" className="px-4 py-2 text-sm">
                {category}
              </Badge>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {additionalServices.map((service, index) => (
              <div key={index}>
                <Card className="h-full bg-gradient-card border-0 shadow-card hover:shadow-elegant transition-all duration-300 hover:-translate-y-1 group">
                  <div className="relative h-48 overflow-hidden rounded-t-lg">
                    <OptimizedImage
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-4 right-4">
                      <Badge className="bg-primary/90 backdrop-blur-sm">
                        {service.category}
                      </Badge>
                    </div>
                  </div>
                  
                  <CardHeader className="p-6 md:p-8">
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-gradient-to-br from-primary to-primary/60 rounded-xl shrink-0">
                        <service.icon className="h-6 w-6 text-primary-foreground" />
                      </div>
                      <div className="flex-1">
                        <CardTitle className="text-xl md:text-2xl mb-2 group-hover:text-primary transition-colors">
                          {service.title}
                        </CardTitle>
                        <p className="text-muted-foreground text-sm md:text-base">
                          {service.description}
                        </p>
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent className="px-6 md:px-8 pb-6 md:pb-8">
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-semibold mb-3 text-foreground">Key Features</h4>
                        <div className="flex flex-wrap gap-2">
                          {service.features.map((feature, idx) => (
                            <Badge key={idx} variant="secondary" className="text-xs">
                              {feature}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      <Collapsible 
                        open={expandedServices.includes(index)}
                        onOpenChange={() => toggleService(index)}
                      >
                        <CollapsibleTrigger asChild>
                          <Button 
                            variant="ghost" 
                            className="w-full justify-between hover:bg-primary/5"
                          >
                            <span className="text-primary font-medium">
                              {expandedServices.includes(index) ? "Show Less" : "Learn More"}
                            </span>
                            <ChevronDown 
                              className={`h-4 w-4 text-primary transition-transform duration-200 ${
                                expandedServices.includes(index) ? "rotate-180" : ""
                              }`}
                            />
                          </Button>
                        </CollapsibleTrigger>
                        <CollapsibleContent className="space-y-6 pt-6">
                          <div>
                            <h4 className="font-semibold mb-3 text-foreground">Overview</h4>
                            <p className="text-muted-foreground text-sm leading-relaxed">
                              {service.details.overview}
                            </p>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <h4 className="font-semibold mb-3 text-foreground">Key Benefits</h4>
                              <ul className="space-y-2">
                                {service.details.benefits.map((benefit, idx) => (
                                  <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                                    <span className="text-primary mt-0.5">•</span>
                                    <span>{benefit}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>

                            <div>
                              <h4 className="font-semibold mb-3 text-foreground">Technologies</h4>
                              <ul className="space-y-2">
                                {service.details.technologies.map((tech, idx) => (
                                  <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                                    <span className="text-primary mt-0.5">•</span>
                                    <span>{tech}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>

                          <div>
                            <h4 className="font-semibold mb-3 text-foreground">Our Process</h4>
                            <div className="flex flex-wrap gap-2">
                              {service.details.process.map((step, idx) => (
                                <Badge key={idx} variant="outline" className="text-xs">
                                  {idx + 1}. {step}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default AllServices;